import React, { useState, useEffect } from 'react';
import { Modal, Button, Dropdown, Header, Message } from 'semantic-ui-react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Timestamp } from 'firebase/firestore';

interface User {
    id: string;
    firstName: string;
    lastName: string;
    initiatedName?: string;
    role: 'student' | 'grader';
}

interface Submission {
    studentId: string;
    submittedAt?: Date;
    assignedGraderId?: string;
    assignedGraderName?: string;
    assignedAt?: Date;
    gradingStatus?: 'pending' | 'graded';
    grade?: number;
    requiresRewrite?: boolean;
    essay?: string;
    criteriaGrades?: unknown[];
    graderComments?: string;
    submitted?: boolean;
    pdfUrl?: string;
    answers?: { shortAnswer?: unknown[]; longAnswer?: unknown[] } | Array<{ assignedGraderId?: string; assignedAt?: Date; gradingStatus?: string; [key: string]: unknown }>;
    answer?: string;
    grades?: {
        shortAnswer: number | Array<{ questionId: string; grade: number; maxGrade: number; comment?: string }>;
        longAnswer: number | Array<{ questionId: string; grade: number; maxGrade: number; comment?: string }>;
    };
    graderRemovedFromModule?: boolean;
}

interface ReassignModalProps {
    isOpen: boolean;
    onClose: () => void;
    submission: Submission;
    studentId: string;
    testId: string;
    moduleId: string;
    availableGraders: User[];
    currentGrader?: User;
    onReassignSuccess: (updatedSubmissionData: any, studentId: string, testId: string) => void;
}

const ReassignModal: React.FC<ReassignModalProps> = ({
    isOpen,
    onClose,
    submission,
    studentId,
    testId,
    moduleId,
    availableGraders,
    currentGrader,
    onReassignSuccess
}) => {
    const [selectedGraderId, setSelectedGraderId] = useState<string>('');
    const [isReassigning, setIsReassigning] = useState(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');

    // Initialize selected grader when modal opens
    useEffect(() => {
        if (isOpen && currentGrader) {
            setSelectedGraderId(currentGrader.id);
        }
    }, [isOpen, currentGrader]);

    // Reset states when modal closes
    useEffect(() => {
        if (!isOpen) {
            setError('');
            setSuccess('');
            setIsReassigning(false);
        }
    }, [isOpen]);

    const handleReassign = async () => {
        if (!selectedGraderId || selectedGraderId === currentGrader?.id) {
            setError('Please select a different grader');
            return;
        }

        setIsReassigning(true);
        setError('');
        setSuccess('');

        try {
            const submissionRef = doc(db, 'modules', moduleId, 'tests', testId, 'submissions', studentId);
            
            // First, get the current document to preserve all existing data
            const currentDoc = await getDoc(submissionRef);
            if (!currentDoc.exists()) {
                throw new Error('Submission document not found');
            }

            const currentData = currentDoc.data();
            const selectedGrader = availableGraders.find(g => g.id === selectedGraderId);
            
            if (!selectedGrader) {
                throw new Error('Selected grader not found');
            }

            const graderName = selectedGrader.initiatedName || `${selectedGrader.firstName} ${selectedGrader.lastName}`;
            const now = Timestamp.now();

            // Determine if this is an Open Book test (has answers array) or Close Book test
            const isOpenBookTest = currentData.answers && Array.isArray(currentData.answers);

            if (isOpenBookTest) {
                // Open Book Test: Update the answers array
                const updatedAnswers = [...currentData.answers];
                if (updatedAnswers.length > 0) {
                    updatedAnswers[0] = {
                        ...updatedAnswers[0], // Preserve all existing fields
                        assignedGraderId: selectedGraderId,
                        assignedAt: now
                    };
                }

                await updateDoc(submissionRef, {
                    answers: updatedAnswers
                });
            } else {
                // Close Book / Verse Memorization: Update root-level fields
                await updateDoc(submissionRef, {
                    assignedGraderId: selectedGraderId,
                    assignedGraderName: graderName,
                    assignedAt: now
                });
            }

            // Fetch the updated submission data to pass back to parent
            const updatedDoc = await getDoc(submissionRef);
            if (updatedDoc.exists()) {
                const updatedSubmissionData = updatedDoc.data() as Submission;
                
                setSuccess('Submission reassigned successfully!');
                setTimeout(() => {
                    onReassignSuccess(updatedSubmissionData, studentId, testId);
                    onClose();
                }, 1500);
            } else {
                throw new Error('Could not fetch updated submission data');
            }

        } catch (err) {
            console.error('Error reassigning submission:', err);
            setError(err instanceof Error ? err.message : 'Failed to reassign submission');
        } finally {
            setIsReassigning(false);
        }
    };

    const graderOptions = availableGraders.map(grader => ({
        key: grader.id,
        value: grader.id,
        text: grader.initiatedName || `${grader.firstName} ${grader.lastName}`,
        content: (
            <div>
                <div>{grader.initiatedName || `${grader.firstName} ${grader.lastName}`}</div>
                <div style={{ fontSize: '0.8em', color: '#666' }}>
                    {grader.firstName} {grader.lastName}
                </div>
            </div>
        )
    }));

    return (
        <Modal open={isOpen} onClose={onClose} size="small">
            <Modal.Header>Reassign Submission</Modal.Header>
            <Modal.Content>
                {error && <Message negative>{error}</Message>}
                {success && <Message positive>{success}</Message>}
                
                <Header as="h4">Current Assignment</Header>
                <p>
                    <strong>Current Grader:</strong> {
                        currentGrader 
                            ? (currentGrader.initiatedName || `${currentGrader.firstName} ${currentGrader.lastName}`)
                            : 'Not assigned'
                    }
                </p>
                <p>
                    <strong>Student ID:</strong> {studentId.slice(-8)}
                </p>
                <p>
                    <strong>Status:</strong> {submission.gradingStatus || 'pending'}
                </p>

                <Header as="h4">Select New Grader</Header>
                <Dropdown
                    placeholder="Select a grader"
                    fluid
                    selection
                    options={graderOptions}
                    value={selectedGraderId}
                    onChange={(e, { value }) => setSelectedGraderId(value as string)}
                    disabled={isReassigning}
                />
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose} disabled={isReassigning}>
                    Cancel
                </Button>
                <Button 
                    primary 
                    onClick={handleReassign}
                    loading={isReassigning}
                    disabled={!selectedGraderId || selectedGraderId === currentGrader?.id}
                >
                    Reassign
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default ReassignModal;
